import { useRouter } from 'next/router'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getBugsReport } from 'api/reports'
import { useProjectPermissions } from 'hooks/useProjectPermissions'
import { ProjectType } from 'store/constants'
import { openUserRoleModalAction, openLinkModalAction } from 'store/modals'
import { currentUserSelector } from 'store/users'
import { getLinkWithQuery } from '../utils'

export function useMenu() {
  const router = useRouter()
  const dispatch = useDispatch()
  const projectSlug = router.query.projectSlug
  const currentUser = useSelector(currentUserSelector)
  const {
    projectInfo,
    isAdmin,
    isManagerOrAdmin,
    canEditProject,
    canShowDashboard,
  } = useProjectPermissions(projectSlug)
  const canViewProjectControls = !!projectInfo?.is_archived
    ? canEditProject || canShowDashboard
    : canEditProject
  const canSeeCanGrade = isAdmin || currentUser?.can_grade
  const canSeePmp = isAdmin || currentUser?.can_see_pmp
  const canSeePmc =
    isAdmin ||
    currentUser?.can_view_department_report ||
    currentUser?.is_head_of_department
  const [projectMenu, setProjectMenu] = useState([])
  const [generalMenu, setGeneralMenu] = useState([])
  const [editProjectOpened, setEditProjectOpened] = useState(false)

  const isProjectMember = projectInfo?.users?.some(
    (user) => user.id === currentUser?.id
  )

  const isProjectAccessDenied =
    Boolean(projectInfo?.users && currentUser) && !isAdmin && !isProjectMember

  const openUserRoleModal = useCallback(() => {
    dispatch(openUserRoleModalAction({ projectSlug }))
  }, [dispatch, projectSlug])

  const reportUrl = useMemo(() => {
    return getLinkWithQuery(`/projects/${projectSlug}/report`, {
      project: projectInfo?.id,
      group: 'user',
      hideProjectSelect: 1,
      hideUserSelect: false,
      getReport: 1,
      user: '',
    })
  }, [projectSlug, projectInfo?.id])

  const visibleEstimateMenuLink =
    ProjectType.Fix === projectInfo?.project_type?.id

  const getBugsReportHandle = useCallback(async () => {
    if (projectInfo?.id) {
      await getBugsReport(projectInfo.id)
    }
  }, [projectInfo?.id])

  const openWikiModal = useCallback(() => {
    dispatch(openLinkModalAction({ title: 'Яндекс Вики', type: 'wiki' }))
  }, [dispatch])

  const openGanttModal = useCallback(() => {
    dispatch(
      openLinkModalAction({ title: 'План-график проекта', type: 'gantt' })
    )
  }, [dispatch])

  useEffect(() => {
    const analyticsChildren = [
      {
        title: 'Отчет по тестированию',
        onClick: getBugsReportHandle,
      },
      {
        title: 'Время',
        href: reportUrl,
        asHref: `/projects/[projectSlug]/report`,
      },
      {
        title: 'История',
        href: `/projects/${projectSlug}/history`,
        asHref: `/projects/[projectSlug]/history`,
      },
    ].filter(Boolean)

    setProjectMenu(
      [
        {
          icon: 'kanban',
          title: 'Канбан',
          href: `/projects/${projectSlug}`,
          asHref: `/projects/[projectSlug]`,
          iconColor: 'var(--p-blue-580)',
        },
        {
          icon: 'my-tasks',
          title: 'Задачи',
          href: `/projects/${projectSlug}/tasks`,
          asHref: `/projects/[projectSlug]/tasks`,
          iconColor: 'var(--p-blue-580)',
        },
        !isProjectAccessDenied && {
          icon: 'wiki',
          title: 'Яндекс Вики',
          onClick: openWikiModal,
          iconColor: 'var(--p-blue-580)',
          iconHref: '/images/icons/wiki.svg',
        },
        !isProjectAccessDenied && {
          icon: 'projects',
          title: 'План-график проекта',
          onClick: openGanttModal,
          iconColor: 'var(--p-blue-580)',
          iconHref: '/images/icons/ganttpro.svg',
        },
        canViewProjectControls && {
          icon: 'performers',
          title: 'Участники',
          onClick: openUserRoleModal,
          iconColor: 'var(--p-blue-580)',
        },
        canEditProject &&
          visibleEstimateMenuLink && {
            icon: 'estimates',
            title: 'Сметы',
            href: `/projects/${projectSlug}/estimates`,
            asHref: `/projects/[projectSlug]/estimates`,
            iconColor: 'var(--p-blue-580)',
          },
        isManagerOrAdmin && {
          key: 'analytics',
          icon: 'analytics',
          title: 'Аналитика',
          iconColor: 'var(--p-blue-580)',
          children: analyticsChildren,
        },
        !isManagerOrAdmin && {
          icon: 'time',
          title: 'Время',
          href: reportUrl,
          asHref: `/projects/[projectSlug]/report`,
          iconColor: 'var(--p-blue-580)',
        },
        {
          icon: 'backlog',
          title: 'Бэклог',
          href: `/projects/${projectSlug}/backlog`,
          asHref: `/projects/[projectSlug]/backlog`,
          iconColor: 'var(--p-blue-580)',
        },
        canSeePmp && {
          icon: 'pup',
          title: 'Панель управления проектом',
          href: `/projects/${projectSlug}/project-management-panel`,
          asHref: `/projects/[projectSlug]/project-management-panel`,
          iconColor: 'var(--p-blue-580)',
        },
        canViewProjectControls && {
          icon: 'settings',
          title: 'Настройки',
          onClick: () => setEditProjectOpened(true),
          iconColor: 'var(--p-blue-580)',
        },
      ].filter(Boolean)
    )
  }, [
    canEditProject,
    canViewProjectControls,
    canSeePmp,
    visibleEstimateMenuLink,
    isManagerOrAdmin,
    reportUrl,
    getBugsReportHandle,
    openWikiModal,
    openGanttModal,
    openUserRoleModal,
  ])

  useEffect(() => {
    const productionManagementCenterChildren = [
      {
        title: 'Производственный отчет',
        href: '/production-management-center/production-report',
        asHref: '/production-management-center/production-report',
      },
      {
        title: 'Рентабельность отдела',
        href: `/production-management-center/department-profitability`,
        asHref: `/production-management-center/department-profitability`,
      },
      // TODO вернуть на релизе 3,4 панелей
      // {
      //   title: 'Утилизация времени',
      //   href: '/production-management-center/time-utilization',
      //   asHref: '/production-management-center/time-utilization',
      // },
      // {
      //   title: 'Проекты отделов',
      //   href: '/production-management-center/department-projects',
      //   asHref: '/production-management-center/department-projects',
      // },
      {
        title: 'Сводный отчет',
        href: '/production-management-center/summary-report',
        asHref: '/production-management-center/summary-report',
      },
    ].filter(Boolean)

    const dictionariesChildren = [
      {
        title: 'Теги',
        href: `/tags`,
        asHref: `/tags`,
      },
      {
        title: 'Шаблоны',
        href: `/templates`,
        asHref: `/templates`,
      },
    ].filter(Boolean)

    const gradesChildren = [
      canSeeCanGrade && {
        title: 'Справочник грейдов',
        href: `/grades`,
        asHref: `/grades`,
      },
      canSeeCanGrade && {
        title: 'Грейдирование',
        href: `/graduation`,
        asHref: `/graduation`,
      },
      {
        title: 'Грейды',
        href: `/grades/list`,
        asHref: `/grades/list`,
      },

      {
        title: 'Мой грейд',
        href: `/my-grade/${currentUser?.id}`,
        asHref: `/my-grade/[id]`,
      },
    ].filter(Boolean)

    setGeneralMenu(
      [
        {
          icon: 'projects',
          title: 'Проекты',
          href: `/projects`,
          asHref: `/projects`,
          iconColor: 'var(--p-blue-580)',
        },
        {
          icon: 'my-tasks',
          title: 'Мои задачи',
          href: `/users/${currentUser?.id}`,
          asHref: `/users/[id]`,
          iconColor: 'var(--p-blue-580)',
        },
        {
          icon: 'user',
          title: 'Пользователи',
          href: `/users`,
          asHref: `/users`,
          iconColor: 'var(--p-blue-580)',
        },
        {
          icon: 'reports',
          title: 'Отчеты',
          href: `/reports`,
          asHref: `/reports`,
          iconColor: 'var(--p-blue-580)',
        },
        isAdmin && {
          icon: 'ppp-sidebar',
          title: 'Панель проектного портфеля',
          href: `/project-portfolio-panel`,
          asHref: `/project-portfolio-panel`,
          iconColor: 'var(--p-blue-580)',
        },
        canSeePmc && {
          key: 'pmc',
          icon: 'pmc',
          title: 'Центр управления производством',
          iconColor: 'var(--p-blue-580)',
          children: productionManagementCenterChildren,
        },
        {
          icon: 'gantt',
          title: 'Диаграмма Ганта',
          href: `/gantt`,
          asHref: `/gantt`,
          iconColor: 'var(--p-blue-580)',
        },
        isAdmin && {
          key: 'dictionaries',
          icon: 'grades',
          title: 'Справочники',
          iconColor: 'var(--p-blue-580)',
          children: dictionariesChildren,
        },
        {
          key: 'grades',
          icon: 'gradesList',
          title: 'Грейды',
          iconColor: 'var(--p-blue-580)',
          children: gradesChildren,
        },
        !isAdmin && {
          icon: 'tags',
          title: 'Теги',
          href: `/tags`,
          asHref: `/tags`,
          iconColor: 'var(--p-blue-580)',
        },
        {
          icon: 'bug-report',
          title: 'Сообщить об ошибке',
          href: `/bug-report`,
          asHref: `/bug-report`,
          iconColor: 'var(--p-blue-580)',
        },
      ].filter(Boolean)
    )
  }, [currentUser, isAdmin, canSeeCanGrade, canSeePmc])

  return {
    generalMenu,
    projectMenu,
    editProjectOpened,
    setEditProjectOpened,
  }
}
